import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowUpRight,
  Building2,
  CircleDollarSign,
  Coins,
  MapPin,
  Percent,
  Ruler,
  ShieldCheck,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { BASE_URL } from "../../../../config/config";


const getImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${BASE_URL}/uploads/${path.replace(/^\/+/, "")}`;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency: "AED",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const InvestorPortfolioDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [portfolio, setPortfolio] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
const location = useLocation();
  const Id = location.state?.id;
  const fetchPortfolio = async () => {
    setIsLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("rwatoken");

      const response = await axios.get(
        `${BASE_URL}/investor/me/portfolio/${Id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPortfolio(response?.data || null);
    } catch (err) {
      console.log("Failed to fetch investor portfolio:", err);
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Unable to load your investment details."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (Id) fetchPortfolio();
  }, [Id]);

  const property = portfolio?.property;
  const holding = portfolio?.holding;

  const ownershipProgress = useMemo(() => {
    const value = Number(holding?.ownership_pct || 0);
    return Math.min(Math.max(value, 0), 100);
  }, [holding?.ownership_pct]);

  const fundingProgress = useMemo(() => {
    const value = Number(property?.funding_pct || 0);
    return Math.min(Math.max(value, 0), 100);
  }, [property?.funding_pct]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#fdfbf5] text-[#1f2318]">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -left-32 top-24 h-80 w-80 rounded-full bg-[#dfead2]/70 blur-3xl" />
          <div className="absolute right-0 top-40 h-96 w-96 rounded-full bg-[#e9e5cf]/70 blur-3xl" />
        </div>

        <div className="relative flex min-h-screen items-center justify-center px-6">
          <div className="flex flex-col items-center gap-4">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#dfe8d3] border-t-[#4c7a2e]" />
            <p className="text-sm font-medium text-[#777967]">
              Loading your investment...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !portfolio) {
    return (
      <div className="min-h-screen bg-[#fdfbf5] text-[#1f2318]">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -left-32 top-24 h-80 w-80 rounded-full bg-[#dfead2]/70 blur-3xl" />
          <div className="absolute right-0 top-40 h-96 w-96 rounded-full bg-[#e9e5cf]/70 blur-3xl" />
        </div>

        <div className="relative mx-auto flex min-h-screen w-full max-w-[900px] items-center justify-center px-5">
          <div className="w-full rounded-[32px] border border-white/80 bg-white/75 p-8 text-center shadow-[0_25px_80px_rgba(55,92,32,0.10)] backdrop-blur-2xl">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#e9f0df] text-[#4c7a2e]">
              <Wallet size={26} />
            </div>

            <h2 className="font-['Playfair_Display'] text-3xl font-semibold">
              Investment not found
            </h2>

            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-[#777967]">
              {error || "We could not find the investment you requested."}
            </p>

            <button
              onClick={() => navigate(-1)}
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-[#4c7a2e]/20 transition hover:-translate-y-0.5"
            >
              <ArrowLeft size={17} />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const mainImage = getImageUrl(property?.image_paths?.[0]);
  const secondaryImage = getImageUrl(property?.image_paths?.[1]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#fdfbf5] text-[#1f2318]">
      {/* Background atmosphere */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -left-40 top-20 h-[420px] w-[420px] rounded-full bg-[#dfead2]/65 blur-3xl" />
        <div className="absolute right-[-120px] top-[30%] h-[520px] w-[520px] rounded-full bg-[#e9e5cf]/70 blur-3xl" />
        <div className="absolute bottom-[-180px] left-[35%] h-[420px] w-[420px] rounded-full bg-[#e5ead7]/60 blur-3xl" />
      </div>

      <main className="relative mx-auto w-full max-w-[1800px] px-5 py-6 sm:px-8 lg:px-12 xl:px-16">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="group inline-flex items-center gap-2 rounded-full border border-[#dfe5d6] bg-white/75 px-4 py-2.5 text-sm font-semibold text-[#375c20] shadow-sm backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white"
          >
            <ArrowLeft
              size={17}
              className="transition-transform group-hover:-translate-x-0.5"
            />
            Back to Portfolio
          </button>

          <div className="hidden items-center gap-2 rounded-full border border-[#dfe5d6] bg-white/70 px-4 py-2 text-xs font-semibold text-[#4c7a2e] shadow-sm backdrop-blur-xl sm:flex">
            <ShieldCheck size={15} />
            Your Investment
          </div>
        </div>

        {/* Hero */}
        <section className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] xl:gap-16">
          <div>
            <span className="inline-flex rounded-full border border-[#cddcbd] bg-[#eef4e7] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#4c7a2e]">
              Investment Overview
            </span>

            <h1 className="mt-5 max-w-4xl font-['Playfair_Display'] text-4xl font-semibold leading-[1.05] tracking-[-0.03em] text-[#1f2318] sm:text-5xl lg:text-6xl">
              Your investment in{" "}
              <span className="text-[#4c7a2e]">
                {holding?.property_name || property?.name}
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-base leading-7 text-[#6f7165] sm:text-lg">
              View your ownership, invested property details, funding progress,
              and rental income from one place.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/80 bg-white/70 px-4 py-2.5 text-sm font-semibold text-[#4c7a2e] shadow-sm backdrop-blur-xl">
                <MapPin size={16} />
                {property?.location || "Location unavailable"}
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-white/80 bg-white/70 px-4 py-2.5 text-sm font-semibold text-[#4c7a2e] shadow-sm backdrop-blur-xl">
                <Building2 size={16} />
                {property?.property_type || "Property"}
              </div>
            </div>
          </div>

          {/* Same visual language as Terra landing page */}
          <div className="relative min-h-[430px] sm:min-h-[500px]">
            {mainImage ? (
              <div className="absolute right-0 top-0 h-[390px] w-[82%] overflow-hidden rounded-[34px] border border-white/80 bg-white/50 shadow-[0_30px_80px_rgba(55,92,32,0.16)] sm:h-[460px]">
                <img
                  src={mainImage}
                  alt={property?.name || "Investment property"}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1f2318]/30 via-transparent to-transparent" />
              </div>
            ) : (
              <div className="absolute right-0 top-0 flex h-[390px] w-[82%] items-center justify-center rounded-[34px] bg-[#e9f0df] sm:h-[460px]">
                <Building2 size={54} className="text-[#6a7847]" />
              </div>
            )}

            {secondaryImage && (
              <div className="absolute bottom-0 left-0 h-[190px] w-[42%] overflow-hidden rounded-[28px] border-[7px] border-[#fdfbf5] bg-white shadow-[0_25px_55px_rgba(55,92,32,0.16)] sm:h-[220px]">
                <img
                  src={secondaryImage}
                  alt="Property exterior"
                  className="h-full w-full object-cover"
                />
              </div>
            )}

            <div className="absolute left-[28%] top-10 rounded-2xl border border-white/70 bg-white/75 px-5 py-4 shadow-xl shadow-[#375c20]/10 backdrop-blur-2xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#777967]">
                Ownership
              </p>
              <p className="mt-1 text-2xl font-bold text-[#375c20]">
                {formatNumber(holding?.ownership_pct)}%
              </p>
            </div>

            <div className="absolute bottom-8 right-3 rounded-2xl border border-white/70 bg-white/80 px-5 py-4 shadow-xl shadow-[#375c20]/10 backdrop-blur-2xl sm:right-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#777967]">
                Rental income
              </p>
              <p className="mt-1 text-xl font-bold text-[#375c20]">
                {formatCurrency(holding?.rent_earned_aed)}
              </p>
            </div>
          </div>
        </section>

        {/* Investment metrics */}
        <section className="mt-14 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            icon={<Coins size={20} />}
            label="Tokens Held"
            value={formatNumber(holding?.blocks_held)}
            description={`Out of ${formatNumber(property?.total_tokens)} total tokens`}
          />

          <MetricCard
            icon={<Percent size={20} />}
            label="Ownership"
            value={`${formatNumber(holding?.ownership_pct)}%`}
            description="Your share of this property"
          />

          <MetricCard
            icon={<CircleDollarSign size={20} />}
            label="Rent Earned"
            value={formatCurrency(holding?.rent_earned_aed)}
            description="Rental income recorded"
          />

          <MetricCard
            icon={<TrendingUp size={20} />}
            label="Target Yield"
            value={`${formatNumber(property?.gross_annual_yield_pct)}%`}
            description="Gross annual yield"
          />
        </section>
        {
          holding?.rent_earned_aed > 0 
          && (
            <div className="relative z-[100] overflow-visible">
              <RentHistoryCard rentHistory={holding?.rent_history} />
            </div>
          )        
          }
            
        {/* Ownership progress */}
        <section className="mt-6 grid gap-6 lg:grid-cols-2">
          <ProgressCard
            title="Your Ownership"
            value={ownershipProgress}
            label={`${formatNumber(holding?.blocks_held)} tokens held`}
            subLabel={`${formatNumber(holding?.ownership_pct)}% ownership`}
          />

          <ProgressCard
            title="Property Funding"
            value={fundingProgress}
            label={`${formatNumber(property?.blocks_sold)} tokens sold`}
            subLabel={`${formatNumber(property?.funding_pct)}% funded`}
          />
        </section>

        {/* Property + investment details */}
        <section className="mt-6 grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="rounded-[30px] border border-white/80 bg-white/70 p-6 shadow-[0_25px_70px_rgba(55,92,32,0.08)] backdrop-blur-2xl sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#6a7847]">
                  Property Details
                </p>
                <h2 className="mt-2 font-['Playfair_Display'] text-3xl font-semibold text-[#1f2318]">
                  {property?.name}
                </h2>
              </div>

              <div className="hidden h-11 w-11 items-center justify-center rounded-2xl bg-[#e9f0df] text-[#4c7a2e] sm:flex">
                <Building2 size={21} />
              </div>
            </div>

            <p className="mt-5 text-sm leading-7 text-[#777967]">
              {property?.description || "No property description available."}
            </p>

            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              <DetailItem
                icon={<MapPin size={18} />}
                label="Location"
                value={property?.location || "—"}
              />
              <DetailItem
                icon={<Building2 size={18} />}
                label="Property Type"
                value={property?.property_type || "—"}
              />
              <DetailItem
                icon={<Ruler size={18} />}
                label="Area"
                value={`${formatNumber(property?.area_sqft)} sq ft`}
              />
              <DetailItem
                icon={<CircleDollarSign size={18} />}
                label="Property Value"
                value={formatCurrency(property?.property_value_aed)}
              />
            </div>
          </div>

          <div className="rounded-[30px] bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] p-6 text-white shadow-[0_25px_70px_rgba(55,92,32,0.18)] sm:p-8">
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-white/65">
                    Your Holding
                  </p>
                  <h2 className="mt-2 font-['Playfair_Display'] text-3xl font-semibold">
                    Investment Summary
                  </h2>
                </div>

                <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/10">
                  <Wallet size={21} />
                </div>
              </div>

              <div className="mt-8 space-y-4">
                <SummaryRow
                  label="Tokens held"
                  value={formatNumber(holding?.blocks_held)}
                />
                <SummaryRow
                  label="Ownership"
                  value={`${formatNumber(holding?.ownership_pct)}%`}
                />
                <SummaryRow
                  label="Price / token"
                  value={formatCurrency(property?.price_per_block_aed)}
                />
                <SummaryRow
                  label="Rent earned"
                  value={formatCurrency(holding?.rent_earned_aed)}
                />
              </div>


              <div className="mt-auto pt-8">
                <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-xl">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
                      <ShieldCheck size={19} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Investment secured</p>
                      <p className="mt-0.5 text-xs text-white/65">
                        Your portfolio details are linked to your account.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ready to Exit */}
        {Number(holding?.blocks_held) > 0 && (
          <section className="mt-6 overflow-hidden rounded-[28px] border border-white/80 bg-white/70 p-6 shadow-[0_20px_60px_rgba(55,92,32,0.07)] backdrop-blur-2xl sm:p-8">
            <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#6a7847]">
                  Ready to exit?
                </p>
                <h2 className="mt-2 font-['Playfair_Display'] text-2xl font-semibold text-[#1f2318] sm:text-3xl">
                  Sell your tokens back to the market.
                </h2>
                <p className="mt-2 text-sm text-[#777967]">
                  List your holding for resale. Receive AED at current market
                  rate.
                </p>
              </div>

              <button
                onClick={() =>
                  navigate("/customer/portfolio/sell", {
                    state: { id: property?.id },
                  })
                }
                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#4c7a2e]/20 transition hover:-translate-y-0.5"
              >
                Sell Tokens
                <ArrowUpRight size={17} />
              </button>
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <section className="mt-8 overflow-hidden rounded-[32px] border border-[#dfe7d5] bg-[#eef4e7]/75 p-6 backdrop-blur-xl sm:p-8">
          <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#6a7847]">
                Keep growing
              </p>
              <h2 className="mt-2 font-['Playfair_Display'] text-3xl font-semibold text-[#1f2318]">
                Explore more real estate opportunities.
              </h2>
              <p className="mt-2 text-sm text-[#777967]">
                Discover other properties available for investment on this platform.
              </p>
            </div>

            <button
              onClick={() => navigate("/customer/home")}
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#4c7a2e]/20 transition hover:-translate-y-0.5"
            >
              Explore Properties
              <ArrowUpRight size={17} />
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

const MetricCard = ({ icon, label, value, description }) => (
  <div className="rounded-[26px] border border-white/80 bg-white/70 p-5 shadow-[0_20px_55px_rgba(55,92,32,0.07)] backdrop-blur-2xl transition hover:-translate-y-1 hover:shadow-[0_25px_65px_rgba(55,92,32,0.11)]">
    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#e9f0df] text-[#4c7a2e]">
      {icon}
    </div>
    <p className="mt-5 text-xs font-bold uppercase tracking-[0.12em] text-[#777967]">
      {label}
    </p>
    <p className="mt-1 font-['Playfair_Display'] text-2xl font-semibold text-[#1f2318]">
      {value}
    </p>
    <p className="mt-1 text-xs text-[#898b7d]">{description}</p>
  </div>
);

const RentHistoryCard = ({ rentHistory = [] }) => {
  // Current year
  const currentYear = new Date().getFullYear();
  const [isMonthOpen, setIsMonthOpen] = useState(false);

  // January to December
  const allMonths = Array.from({ length: 12 }, (_, index) => {
    const month = String(index + 1).padStart(2, "0");
    const period = `${currentYear}-${month}`;

    const rentData = rentHistory.find(
      (item) => item.rent_period === period
    );

    return {
      rent_period: period,
      amount_aed: rentData?.amount_aed ?? 0,
    };
  });

  // Default selected month = current month
  const currentMonth = `${currentYear}-${String(
    new Date().getMonth() + 1
  ).padStart(2, "0")}`;

  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  // Selected month's rent
  const selectedRent = allMonths.find(
    (item) => item.rent_period === selectedMonth
  );

  const formatMonth = (period) => {
    if (!period) return "";

    const [year, month] = period.split("-");

    return new Date(
      Number(year),
      Number(month) - 1
    ).toLocaleString("en-US", {
      month: "long",
    });
  };

  return (
    <div className="mt-6 w-full rounded-[28px] border border-white/80 bg-white/75 p-5 shadow-[0_10px_40px_rgba(27,82,108,0.06)] backdrop-blur-2xl sm:p-6 lg:p-7">

      <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-[1fr_1.25fr_1.2fr] lg:gap-8">

        {/* LEFT */}
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#e8f2e8]">
            <svg
              className="h-5 w-5 text-[#557c59]"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              viewBox="0 0 24 24"
            >
              <rect
                x="3"
                y="4"
                width="18"
                height="17"
                rx="3"
              />

              <path d="M8 2v4M16 2v4M3 10h18" />

              <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
            </svg>
          </div>

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#77818c]">
              Rent Distribution
            </p>

            <h3 className="mt-1 font-serif text-[24px] font-semibold tracking-[-0.02em] text-[#26352d]">
              Monthly Rent
            </h3>

            <p className="mt-2 max-w-[300px] text-[12px] leading-5 text-[#7a857d]">
              View the rental income received for each month
              from this property.
            </p>
          </div>
        </div>

        {/* MONTH DROPDOWN */}
        <div className="lg:border-l lg:border-[#e4e9e4] lg:pl-8">
  <label className="mb-2.5 block text-[11px] font-semibold text-[#59665d]">
    Select Month
  </label>

  <div className="relative">
    {/* Dropdown Button */}
    <button
      type="button"
      onClick={() => setIsMonthOpen((prev) => !prev)}
      className={`flex h-[58px] w-full items-center justify-between rounded-[16px] border bg-white/80 px-4 text-left text-[14px] font-medium text-[#34443a] outline-none transition-all duration-200 ${
        isMonthOpen
          ? "border-[#9bb49e] ring-4 ring-[#e8f1e9]"
          : "border-[#dfe6df] hover:border-[#b8c8b9]"
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Calendar Icon */}
        <svg
          className="h-[18px] w-[18px] text-[#65736a]"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          viewBox="0 0 24 24"
        >
          <rect
            x="3"
            y="4"
            width="18"
            height="17"
            rx="3"
          />

          <path d="M8 2v4M16 2v4M3 10h18" />
        </svg>

        <span>
          {formatMonth(selectedMonth)}
        </span>
      </div>

      {/* Arrow */}
      <svg
        className={`h-4 w-4 text-[#65736a] transition-transform duration-200 ${
          isMonthOpen ? "rotate-180" : ""
        }`}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>

    {/* Custom Dropdown */}
    {isMonthOpen && (
  <div className="absolute left-0 right-0 top-[66px] z-[9999] overflow-hidden rounded-[16px] border border-[#dfe6df] bg-white p-1.5 shadow-[0_18px_45px_rgba(27,82,108,0.15)]">

    <div className="max-h-[280px] overflow-y-auto pr-1">
      {allMonths.map((month) => {
        const isSelected =
          selectedMonth === month.rent_period;

        const hasRent =
          Number(month.amount_aed || 0) > 0;

        return (
          <button
            key={month.rent_period}
            type="button"
            onClick={() => {
              setSelectedMonth(month.rent_period);
              setIsMonthOpen(false);
            }}
            className={`group flex w-full items-center justify-between rounded-[11px] px-3.5 py-3 text-left transition-all duration-150 ${
              isSelected
                ? "bg-[#edf5ed] text-[#245c32]"
                : "text-[#4b584f] hover:bg-[#f5f8f5]"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full ${
                  isSelected
                    ? "bg-[#dcebdc]"
                    : "bg-[#f1f4f1]"
                }`}
              >
                <svg
                  className={`h-4 w-4 ${
                    isSelected
                      ? "text-[#397244]"
                      : "text-[#7b877e]"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  viewBox="0 0 24 24"
                >
                  <rect
                    x="3"
                    y="4"
                    width="18"
                    height="17"
                    rx="3"
                  />

                  <path d="M8 2v4M16 2v4M3 10h18" />
                </svg>
              </div>

              <div>
                <p className="text-[13px] font-medium">
                  {formatMonth(month.rent_period)}
                </p>

                <p className="mt-0.5 text-[10px] text-[#8a948d]">
                  {hasRent
                    ? "Rent available"
                    : "No rent received"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span
                className={`text-[11px] font-semibold ${
                  hasRent
                    ? "text-[#397244]"
                    : "text-[#9aa39d]"
                }`}
              >
                AED {Number(month.amount_aed || 0).toFixed(2)}
              </span>

              {isSelected && (
                <svg
                  className="h-4 w-4 text-[#397244]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="m5 12 4 4L19 6" />
                </svg>
              )}
            </div>
          </button>
        );
      })}
    </div>
  </div>
)}
  </div>
</div>

        {/* SELECTED RENT */}
        <div className="rounded-[18px] border border-[#e1ebe1] bg-[#f5f9f5]/80 p-5">
          <div className="flex items-center justify-between gap-4">

            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#68756c]">
                {formatMonth(selectedMonth)}
              </p>

              <p className="mt-1 text-[27px] font-semibold tracking-[-0.02em] text-[#245c32]">
                AED {Number(selectedRent?.amount_aed || 0)}
              </p>

              <p className="mt-1 text-[11px] text-[#7a857d]">
                Rent received for selected month
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-2 rounded-full bg-[#e0efdf] px-3 py-2">
              <svg
                className="h-4 w-4 text-[#397244]"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                viewBox="0 0 24 24"
              >
                <circle cx="9" cy="9" r="5" />
                <circle cx="15" cy="15" r="5" />
                <path d="M7 9h4M13 15h4" />
              </svg>

              <span className="text-[11px] font-semibold text-[#397244]">
                Monthly
              </span>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

const ProgressCard = ({ title, value, label, subLabel }) => (
  <div className="rounded-[28px] border border-white/80 bg-white/70 p-6 shadow-[0_20px_60px_rgba(55,92,32,0.07)] backdrop-blur-2xl sm:p-7">
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#777967]">
          {title}
        </p>
        <p className="mt-1 text-lg font-semibold text-[#1f2318]">{label}</p>
      </div>
      <span className="font-['Playfair_Display'] text-2xl font-semibold text-[#4c7a2e]">
        {formatNumber(value)}%
      </span>
    </div>

    <div className="mt-5 h-3 overflow-hidden rounded-full bg-[#e6eadf]">
      <div
        className="h-full rounded-full bg-gradient-to-r from-[#6fa142] via-[#4c7a2e] to-[#375c20] transition-all duration-700"
        style={{ width: `${value}%` }}
      />
    </div>

    <p className="mt-3 text-xs text-[#898b7d]">{subLabel}</p>
  </div>
);

const DetailItem = ({ icon, label, value }) => (
  <div className="rounded-2xl border border-[#e4e8de] bg-[#f9faf5]/80 p-4">
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#e9f0df] text-[#4c7a2e]">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#898b7d]">
          {label}
        </p>
        <p className="mt-0.5 truncate text-sm font-semibold text-[#313728]">
          {value}
        </p>
      </div>
    </div>
  </div>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-4 last:border-0 last:pb-0">
    <span className="text-sm text-white/65">{label}</span>
    <span className="text-sm font-semibold text-white">{value}</span>
  </div>
);

export default InvestorPortfolioDetails;